import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { ComparisonResult, VersionDiff, NoChangesNotice, diffTotal } from './VersionDiff';

interface ReviewChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  confirmingLabel: string;
  // Omit when there is no meaningful baseline to diff against (e.g. the
  // first-ever publish of a template) — the dialog then skips straight to
  // the confirm action without implying "no changes."
  loadComparison?: () => Promise<ComparisonResult>;
  onConfirm: () => Promise<void>;
}

// Shared gate for any action that freezes or overwrites template content
// (publish, revert) — forces the diff to load and render before the
// destructive action's confirm button is reachable.
const ReviewChangesDialog: React.FC<ReviewChangesDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  confirmingLabel,
  loadComparison,
  onConfirm,
}) => {
  const [comparison, setComparison] = useState<ComparisonResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setComparison(null);
      setError(null);
      return;
    }
    if (!loadComparison) return;
    setIsLoading(true);
    setError(null);
    loadComparison()
      .then(setComparison)
      .catch(err =>
        setError(err instanceof Error ? err.message : 'Failed to load changes')
      )
      .finally(() => setIsLoading(false));
    // Only reload when the dialog opens, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleConfirm = async () => {
    setIsConfirming(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setIsConfirming(false);
    }
  };

  const changeCount = comparison ? diffTotal(comparison) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 py-1">
          {!loadComparison && (
            <div className="text-xs text-gray-500 bg-gray-50 border rounded px-3 py-2">
              Nothing to compare against yet — this is the first version.
            </div>
          )}
          {loadComparison && isLoading && (
            <div className="text-xs text-gray-500 py-6 text-center">
              Loading changes…
            </div>
          )}
          {loadComparison && !isLoading && error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {loadComparison && !isLoading && !error && comparison && changeCount === 0 && (
            <NoChangesNotice />
          )}
          {loadComparison && !isLoading && !error && comparison && changeCount! > 0 && (
            <VersionDiff comparison={comparison} />
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isConfirming}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={
              isConfirming || (!!loadComparison && (isLoading || !!error || !comparison))
            }
          >
            {isConfirming ? confirmingLabel : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewChangesDialog;
